import moment from 'moment';

function normalizeSearchTerm(value) {
  return String(value || '').trim().toLowerCase();
}

export function itemMatchesSearch(item, search) {
  const query = normalizeSearchTerm(search);
  if (!query) {
    return true;
  }

  const parentName = normalizeSearchTerm(item?.parentName);
  const petName = normalizeSearchTerm(item?.petName);

  return parentName.includes(query) || petName.includes(query);
}

export function getFacilityLocalDayKey(dateTime, utcOffset = 0) {
  if (!dateTime) {
    return '';
  }

  return moment(dateTime)
    .utc()
    .add(Number(utcOffset) || 0, 'minutes')
    .format('YYYY-MM-DD');
}

export function formatGalleryPhotoSentAt(dateTime, utcOffset = 0) {
  if (!dateTime) {
    return '';
  }

  return moment(dateTime)
    .utc()
    .add(Number(utcOffset) || 0, 'minutes')
    .format('MMM D, YYYY · h:mm A');
}

function compareStringsAsc(a, b) {
  return String(a || '').localeCompare(String(b || ''), undefined, {
    sensitivity: 'base',
  });
}

function getSortName(item, sortField) {
  if (sortField === 'patient') {
    return item?.petName || '';
  }

  return item?.parentName || '';
}

function compareMessagesForGallery(a, b, sortField, sortOrder = 1) {
  let nameCompare = compareStringsAsc(
    getSortName(a, sortField),
    getSortName(b, sortField),
  );

  if (sortOrder === -1) {
    nameCompare = -nameCompare;
  }

  if (nameCompare !== 0) {
    return nameCompare;
  }

  const aTime = new Date(a?.dateTime || 0).getTime();
  const bTime = new Date(b?.dateTime || 0).getTime();

  return aTime - bTime;
}

export function buildOrderedGalleryPhotos({
  items = [],
  sortField = 'parent',
  sortOrder = 1,
  search = '',
  utcOffset = 0,
} = {}) {
  const filteredItems = (items || []).filter(
    (item) =>
      itemMatchesSearch(item, search) &&
      Array.isArray(item.gallery) &&
      item.gallery.length > 0,
  );

  const itemsByDay = new Map();

  filteredItems.forEach((item) => {
    const dayKey = getFacilityLocalDayKey(item.dateTime, utcOffset);

    if (!itemsByDay.has(dayKey)) {
      itemsByDay.set(dayKey, []);
    }

    itemsByDay.get(dayKey).push(item);
  });

  const sortedDayKeys = Array.from(itemsByDay.keys()).sort((a, b) =>
    b.localeCompare(a),
  );

  const photos = [];

  sortedDayKeys.forEach((dayKey) => {
    const dayItems = [...itemsByDay.get(dayKey)].sort((a, b) =>
      compareMessagesForGallery(a, b, sortField, sortOrder),
    );

    dayItems.forEach((item) => {
      item.gallery.forEach((url, galleryIndex) => {
        if (!url) {
          return;
        }

        photos.push({
          url,
          messageId: item.messageId,
          dateTime: item.dateTime,
          parentName: item.parentName || '',
          petName: item.petName || '',
          galleryIndex,
        });
      });
    });
  });

  return photos;
}

/** Gallery tile delivery only — does not mutate stored original URLs. */
const CLOUDINARY_UPLOAD_SEGMENT = '/image/upload/';
const GALLERY_TILE_TRANSFORM = 'c_limit,w_456,h_456,q_auto,f_auto';

export function getCloudinaryGalleryTileUrl(url) {
  if (!url || typeof url !== 'string') {
    return url;
  }

  try {
    if (
      !url.includes('res.cloudinary.com') ||
      !url.includes(CLOUDINARY_UPLOAD_SEGMENT)
    ) {
      return url;
    }

    const insertAt =
      url.indexOf(CLOUDINARY_UPLOAD_SEGMENT) + CLOUDINARY_UPLOAD_SEGMENT.length;
    const afterUpload = url.slice(insertAt);

    if (
      afterUpload.startsWith(`${GALLERY_TILE_TRANSFORM}/`) ||
      afterUpload.startsWith(`${GALLERY_TILE_TRANSFORM},`)
    ) {
      return url;
    }

    return `${url.slice(0, insertAt)}${GALLERY_TILE_TRANSFORM}/${afterUpload}`;
  } catch (error) {
    return url;
  }
}
